/**
 * App.tsx — ルーティング設定
 * 認証チェック: /auth/me → 未認証 → /login へリダイレクト
 * focus_mode パス: /practice/session, /mock-exam/session/* → BottomNav 非表示（Layout 内で制御）
 * 経験則 #50: 各ルートに ErrorBoundary
 */

import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useContext } from 'react'
import { AuthProvider } from './contexts/AuthContext'
import { AuthContext } from './contexts/AuthContext'
import ErrorBoundary from './components/ErrorBoundary'
import Layout from './components/Layout'

// Pages
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import CertSelectPage from './pages/CertSelectPage'
import HomePage from './pages/HomePage'
import StudyListPage from './pages/StudyListPage'
import StudyReaderPage from './pages/StudyReaderPage'
import PracticePage from './pages/PracticePage'
import ReviewPage from './pages/ReviewPage'
import MockSetupPage from './pages/MockSetupPage'
import MockExamPage from './pages/MockExamPage'
import MockResultPage from './pages/MockResultPage'
import StatsPage from './pages/StatsPage'

/** 認証ガード: loading 中は null、未認証は /login へ */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useContext(AuthContext)
  const location = useLocation()

  if (loading) return null

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return <>{children}</>
}

/** 種別選択ガード: certType 未設定は /cert-select へ */
function RequireCertType({ children }: { children: React.ReactNode }) {
  const { user, loading, certType } = useContext(AuthContext)
  if (loading) return null
  if (!user) return <Navigate to="/login" replace />
  if (!certType) return <Navigate to="/cert-select" replace />
  return <>{children}</>
}

function AppRoutes() {
  return (
    <Routes>
      {/* 認証不要 */}
      <Route
        path="/login"
        element={
          <ErrorBoundary>
            <LoginPage />
          </ErrorBoundary>
        }
      />
      <Route
        path="/register"
        element={
          <ErrorBoundary>
            <RegisterPage />
          </ErrorBoundary>
        }
      />

      {/* 認証必要・種別選択 */}
      <Route
        path="/cert-select"
        element={
          <RequireAuth>
            <ErrorBoundary>
              <CertSelectPage />
            </ErrorBoundary>
          </RequireAuth>
        }
      />

      {/* 認証必要・種別必要・Layout 付き */}
      <Route
        element={
          <RequireCertType>
            <Layout />
          </RequireCertType>
        }
      >
        <Route
          index
          element={
            <ErrorBoundary>
              <HomePage />
            </ErrorBoundary>
          }
        />
        <Route
          path="study"
          element={
            <ErrorBoundary>
              <StudyListPage />
            </ErrorBoundary>
          }
        />
        <Route
          path="study/:id"
          element={
            <ErrorBoundary>
              <StudyReaderPage />
            </ErrorBoundary>
          }
        />
        <Route
          path="practice"
          element={
            <ErrorBoundary>
              <PracticePage />
            </ErrorBoundary>
          }
        />
        <Route
          path="review"
          element={
            <ErrorBoundary>
              <ReviewPage />
            </ErrorBoundary>
          }
        />
        <Route
          path="mock-exam"
          element={
            <ErrorBoundary>
              <MockSetupPage />
            </ErrorBoundary>
          }
        />
        {/* focus_mode: BottomNav が非表示になるパス（BottomNav.tsx が制御） */}
        <Route
          path="mock-exam/session/:sessionId/:questionOrder"
          element={
            <ErrorBoundary>
              <MockExamPage />
            </ErrorBoundary>
          }
        />
        <Route
          path="mock-exam/result/:sessionId"
          element={
            <ErrorBoundary>
              <MockResultPage />
            </ErrorBoundary>
          }
        />
        <Route
          path="stats"
          element={
            <ErrorBoundary>
              <StatsPage />
            </ErrorBoundary>
          }
        />
      </Route>

      {/* フォールバック */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  )
}
